/**
 * Payload shaping for PUT /api/v1/campaigns/:id/videos/insights.
 *
 * Every field except yt_video_id is optional and must be OMITTED when blank —
 * sending "" would overwrite a real value with nothing. Rates are stored as
 * fractions (0.0345 = 3.45%) but entered as percentages.
 */

/** Plain counts and durations: entered and stored in the same unit. */
export const NUMERIC_FIELDS = [
  'paid_view_count',
  'paid_like_count',
  'paid_comment_count',
  'paid_share_count',
  'paid_watch_time',
  'paid_avg_view_duration',
  'engaged_views',
  'paid_engaged_views',
  'playlist_adds',
  'paid_playlist_adds',
];

/** Entered as a percentage, stored as a fraction. */
export const RATE_FIELDS = ['paid_engagement_rate', 'avg_watch_pct'];

export const BOOLEAN_FIELDS = ['has_product_placement'];

export const TEXT_FIELDS = ['brand_mentions'];

function parseOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildInsightsPayload(youtubeVideoId, form) {
  const source = form && typeof form === 'object' ? form : {};

  const numeric = NUMERIC_FIELDS.reduce((payload, field) => {
    const parsed = parseOrNull(source[field]);
    return parsed === null ? payload : { ...payload, [field]: parsed };
  }, {});

  const rates = RATE_FIELDS.reduce((payload, field) => {
    const parsed = parseOrNull(source[field]);
    return parsed === null ? payload : { ...payload, [field]: parsed / 100 };
  }, {});

  const booleans = BOOLEAN_FIELDS.reduce((payload, field) => {
    return typeof source[field] === 'boolean' ? { ...payload, [field]: source[field] } : payload;
  }, {});

  const text = TEXT_FIELDS.reduce((payload, field) => {
    const value = source[field];
    return value ? { ...payload, [field]: value } : payload;
  }, {});

  return { yt_video_id: youtubeVideoId, ...numeric, ...rates, ...booleans, ...text };
}

/** Seed the form from a video row, reversing the rate conversion for display. */
export function insightsFormFromVideo(video) {
  const paid = (video && video.paid) || {};

  const numeric = NUMERIC_FIELDS.reduce((form, field) => {
    const key = field.replace(/^paid_/, '');
    const value = paid[key] ?? paid[field];
    return { ...form, [field]: value === null || value === undefined ? '' : String(value) };
  }, {});

  const rates = RATE_FIELDS.reduce((form, field) => {
    const key = field.replace(/^paid_/, '');
    const value = paid[key] ?? paid[field];
    const parsed = parseOrNull(value);
    return { ...form, [field]: parsed === null ? '' : String(parsed * 100) };
  }, {});

  return { ...numeric, ...rates, has_product_placement: false, brand_mentions: '' };
}
